import { z } from 'zod'

import { AssignedMedia } from '~/common/schemas/media'
import { MAX_SKU_LENGTH } from '~/constants'
import { UserLocation } from './location'

/**
 * This regex is used to validate the SKU field. It allows all unicode characters
 * and the following special characters: -_"&(),.+!/*
 * */
const SKU_REGEX = new RegExp(/^[\p{L}\p{Mn}\p{Lo}\p{Zs}\p{N}\-_"&(),.+!/*]+$/u)

export const StockItemStatus = z.enum(['active', 'deleted'])
export type StockItemStatus = z.infer<typeof StockItemStatus>

// from stock/models.py
export const BaseStockItem = z.object({
    id: z.number(),
    barcode: z.string().max(64).nullish(),
    sku: z.string().max(256).nullish(),
    should_calculate_low_stock: z.boolean().default(true),
    days_till_empty_threshold: z.number().default(14),
    low_stock_period_size: z.number().default(30),
    should_calculate_slow_selling: z.boolean().default(true),
    units_sold_during_period_threshold: z.number().default(1),
    slow_selling_period_size: z.number().default(10),
    status: StockItemStatus.default(StockItemStatus.enum.active),
    is_bundle: z.boolean().default(false),
    package_width: z.coerce.number().nonnegative().nullish(),
    package_height: z.coerce.number().nonnegative().nullish(),
    package_length: z.coerce.number().nonnegative().nullish(),
    package_weight: z.coerce.number().nonnegative().nullish(),
    buffer_threshold: z.number().default(0),
    owner_id: z.number().nullish(),
})
export type BaseStockItem = z.infer<typeof BaseStockItem>

export const BundleItem = z.object({
    id: z.number(),
    num_of_units: z.coerce.number(),
    member_bundle_item: BaseStockItem.pick({ id: true, sku: true, barcode: true }),
})
export type BundleItem = z.infer<typeof BundleItem>

const StockUnitStockItem = BaseStockItem.pick({
    id: true,
    sku: true,
    is_bundle: true,
    package_width: true,
    package_length: true,
    package_height: true,
    package_weight: true,
    barcode: true,
}).extend({
    bundle_items: z.array(BundleItem),
    fulfillment_config: z
        .object({
            must_ship_separately: z.boolean().optional().nullish(),
            preferred_shipping_provider_id: z.number().optional().nullish(),
        })
        .optional()
        .nullish(),
})
export type StockUnitStockItem = z.infer<typeof StockUnitStockItem>
export const StockUnit = z.object({
    id: z.number(),
    dimension_options: z.array(
        z.object({
            id: z.number(),
            index: z.number(),
            product_dimension: z.object({
                index: z.number(),
            }),
        })
    ),
    images: AssignedMedia.extend({ stock_unit: z.number() }).array(),
    product: z.number(),
    sku: z
        .string()
        .min(1, gettext('SKU is required'))
        .max(MAX_SKU_LENGTH, gettext(`SKU must have less than ${MAX_SKU_LENGTH} characters`))
        .regex(SKU_REGEX, gettext('Invalid SKU')),
    status: z.enum(['active', 'inactive', 'deleted']).optional(),
    stock_item: StockUnitStockItem,
})
export type StockUnit = z.infer<typeof StockUnit>

export const ContentObjectInfo = z.object({
    id: z.number().optional(),
    label: z.string().optional(),
    type: z.string().optional(),
})

export const StockRecordAdjustments = z.object({
    id: z.number().optional(),
    adjustment_type_name: z.string().optional(),
    beginning_quantity: z.number().optional(),
    change_in_quantity: z.number().optional(),
    date_updated: z.string().optional(),
    content_object_info: ContentObjectInfo,
})
export type StockRecordAdjustments = z.infer<typeof StockRecordAdjustments>

export const StockRecord = z.object({
    id: z.number(),
    location: UserLocation,
    stock_item: BaseStockItem,
    quantity: z.coerce.number(),
})
export type StockRecord = z.infer<typeof StockRecord>
